import {restRequest} from '@nlabs/metropolisjs';

import type {FluxFramework} from '@nlabs/arkhamjs';

export type AuthMode = 'sign-in' | 'sign-up' | 'verify-email' | 'resend-verification'
  | 'forgot-password' | 'reset-password' | 'sign-out';
export type AuthOperation = AuthMode | 'session';

export interface AuthUser {
  readonly email: string;
  readonly id: string;
  readonly name: string;
  readonly verifiedEmail: boolean;
}

export interface DemoDelivery {
  readonly kind: 'verification' | 'reset';
  readonly notice: string;
  readonly token: string;
  readonly url: string;
}

export interface AuthResult {
  readonly deliveryMode: 'demo' | 'email';
  readonly demoDelivery?: DemoDelivery;
  readonly message?: string;
  readonly user: AuthUser | null;
}

export interface AuthEvent {
  readonly error?: string;
  readonly operation: AuthOperation;
  readonly result?: AuthResult;
}

export const AUTH_EVENTS = {
  CHANGED: 'REACTORBOX_AUTH_CHANGED',
  ERROR: 'REACTORBOX_AUTH_ERROR',
  PENDING: 'REACTORBOX_AUTH_PENDING'
} as const;

const activeRequests = new WeakMap<FluxFramework, Set<AuthOperation>>();
const sessionRequests = new WeakMap<FluxFramework, Promise<void>>();

const parseAuthResult = (value: unknown, operation: AuthOperation): AuthResult => {
  if(!value || typeof value !== 'object') {
    throw new Error('The account service returned an incomplete response. Please try again.');
  }
  if('error' in value) {
    throw new Error(typeof value.error === 'string' ? value.error : 'Unable to complete this account request.');
  }
  const result = value as AuthResult;
  const {user} = result;
  const validUser = user === null || (user && typeof user.id === 'string' && typeof user.name === 'string'
    && typeof user.email === 'string' && typeof user.verifiedEmail === 'boolean');
  if(!validUser || !['demo', 'email'].includes(result.deliveryMode)) {
    throw new Error('The account service returned an incomplete response. Please try again.');
  }
  if(operation === 'sign-in' && !user?.verifiedEmail) {
    throw new Error('Sign-in could not be confirmed. Please verify your email and try again.');
  }
  if(operation === 'session' && user && !user.verifiedEmail) {
    throw new Error('Your session could not be confirmed. Please sign in again.');
  }
  if(['sign-out', 'reset-password'].includes(operation) && user !== null) {
    throw new Error('The account service did not confirm the end of your session. Please try again.');
  }
  const delivery = result.demoDelivery;
  const safeDelivery = result.deliveryMode === 'demo' && delivery
    && ['verification', 'reset'].includes(delivery.kind) && typeof delivery.token === 'string'
    && typeof delivery.notice === 'string' && typeof delivery.url === 'string' ? delivery : undefined;
  return {
    deliveryMode: result.deliveryMode,
    ...(safeDelivery ? {demoDelivery: safeDelivery} : {}),
    ...(typeof result.message === 'string' ? {message: result.message} : {}),
    user
  };
};

export const createAuthActions = (flux: FluxFramework) => {
  const request = async (operation: AuthOperation, body?: unknown): Promise<void> => {
    const active = activeRequests.get(flux) || new Set<AuthOperation>();
    if(active.has(operation) || (operation !== 'session' && [...active].some((item) => item !== 'session'))) {
      return;
    }
    active.add(operation);
    activeRequests.set(flux, active);
    await flux.setState('auth.error', '');
    await flux.setState('auth.pending', true);
    await flux.dispatch({operation, type: AUTH_EVENTS.PENDING});
    try {
      if(operation !== 'session') {
        // Finish restoration first so an older cookie response cannot undo a new sign-in.
        await sessionRequests.get(flux);
      }
      // Same-origin HttpOnly cookies travel through Metropolis/Rip-Hunter.
      // Passwords stay in the request body and never enter Flux state or events.
      const response = await restRequest<unknown>(flux, `/api/auth/${operation}`,
        operation === 'session' ? 'GET' : 'POST', body, {queueOffline: false});
      const result = parseAuthResult(response, operation);
      if(['session', 'sign-in', 'sign-out', 'reset-password'].includes(operation)) {
        const previous = flux.getState('auth.user') as AuthUser | null | undefined;
        if((previous?.id || null) !== (result.user?.id || null)) {
          const epoch = flux.getState('auth.epoch', 0) as number;
          await flux.setState('auth.epoch', epoch + 1);
        }
        await flux.setState('auth.user', result.user);
      }
      await flux.setState('auth.deliveryMode', result.deliveryMode);
      await flux.setState('auth.result', result);
      await flux.setState('auth.ready', true);
      await flux.setState('auth.pending', active.size > 1);
      await flux.dispatch({operation, result, type: AUTH_EVENTS.CHANGED});
    } catch(error) {
      const message = error instanceof Error ? error.message : 'Unable to reach the account service. Please try again.';
      await flux.setState('auth.error', message);
      await flux.setState('auth.pending', active.size > 1);
      if(operation === 'session') {
        await flux.setState('auth.ready', true);
      }
      await flux.dispatch({error: message, operation, type: AUTH_EVENTS.ERROR});
    } finally {
      active.delete(operation);
    }
  };

  return {
    forgotPassword: (email: string) => request('forgot-password', {email: email.trim()}),
    resendVerification: (email: string) => request('resend-verification', {email: email.trim()}),
    resetPassword: (input: {password: string; token: string}) => request('reset-password', input),
    restoreSession: (): Promise<void> => {
      const pending = sessionRequests.get(flux);
      if(pending) {
        return pending;
      }
      if([...activeRequests.get(flux) || []].some((operation) => operation !== 'session')) {
        return Promise.resolve();
      }
      const session = request('session');
      sessionRequests.set(flux, session);
      void session.finally(() => sessionRequests.delete(flux));
      return session;
    },
    signIn: (input: {email: string; password: string}) => request('sign-in', {...input, email: input.email.trim()}),
    signOut: () => request('sign-out', {}),
    signUp: (input: {email: string; marketingEmails?: boolean; name: string; password: string}) => request('sign-up', {
      ...input, email: input.email.trim(), name: input.name.trim()
    }),
    verifyEmail: (token: string) => request('verify-email', {token: token.trim()})
  };
};

export type AuthActions = ReturnType<typeof createAuthActions>;
